/**
 * SSO (Single Sign-On) Utilities
 * Handles secure authentication flow between Network and App
 */

import axios from './axios';
import { generatePKCEPair, storePKCEVerifier } from './pkce';

/**
 * Initiate SSO flow to Summus App
 * @param {string} returnUrl - URL to return to after SSO (default: app dashboard)
 * @returns {Promise<void>}
 */
export async function initiateSSOToApp(returnUrl = null) {
  try {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🚀 [SSO] STARTING SSO TO APP');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    // Default return URL if not provided
    const appUrl = (process.env.REACT_APP_APP_URL || 'http://app.summus_network.localhost').replace(/\/$/, '');
    const finalReturnUrl = returnUrl || `${appUrl}/dashboard`;

    console.log('[SSO] Configuration');
    console.log('  - App URL:', appUrl);
    console.log('  - Return URL:', finalReturnUrl);
    console.log('  - User authenticated in Network:', !!localStorage.getItem('accessToken'));

    // Use the full PKCE SSO flow directly
    console.log('[SSO] Using PKCE SSO flow...');
    await performSSOFlow('app', finalReturnUrl);
  } catch (error) {
    console.error('❌ [SSO] Error during SSO to App:', error);
    alert('Errore durante la navigazione. Riprova.');
  }
}

/**
 * Initiate SSO flow to Summus Network (reverse flow)
 * @param {string} returnUrl - URL to return to after SSO (default: network dashboard)
 * @returns {Promise<void>}
 */
export async function initiateSSOToNetwork(returnUrl = null) {
  try {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🚀 [SSO] STARTING NAVIGATION TO NETWORK');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    // Default return URL if not provided
    const networkUrl = (process.env.NEXT_PUBLIC_NETWORK_URL || 'http://summus_network.localhost').replace(/\/$/, '');
    const finalReturnUrl = returnUrl || `${networkUrl}/user/dashboard`;

    console.log('[SSO] Step 0: Configuration');
    console.log('  - Network URL:', networkUrl);
    console.log('  - Return URL:', finalReturnUrl);
    console.log('  - User authenticated in App:', !!localStorage.getItem('accessToken'));

    // First, try direct navigation with check_auth flag
    // The Network will check if it has a valid token, if not it will redirect back for SSO
    console.log('[SSO] Step 1: Attempting direct navigation to Network...');
    console.log('  - Network will check its own authentication status');
    const directUrl = `${finalReturnUrl}?from=app`;
    console.log('  - Navigating to:', directUrl);

    window.location.href = directUrl;
  } catch (error) {
    console.error('❌ [SSO] Error navigating to Network:', error);
    alert('Errore durante la navigazione. Riprova.');
  }
}

/**
 * Actually perform the SSO flow (called when authentication is needed)
 * @param {string} targetApp - 'app' or 'network'
 * @param {string} returnUrl - URL to return to after SSO
 * @returns {Promise<void>}
 */
export async function performSSOFlow(targetApp = 'app', returnUrl = null) {
  try {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log(`🔐 [SSO] PERFORMING SSO FLOW TO ${targetApp.toUpperCase()}`);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    const targetUrl = targetApp === 'app'
      ? (process.env.REACT_APP_APP_URL || process.env.NEXT_PUBLIC_FRONTEND_URL || 'http://app.summus_network.localhost')
      : (process.env.REACT_APP_NETWORK_URL || process.env.NEXT_PUBLIC_NETWORK_URL || 'http://summus_network.localhost');

    const finalReturnUrl = returnUrl || `${targetUrl.replace(/\/$/, '')}/dashboard`;

    console.log('[SSO] Configuration:');
    console.log('  - Target:', targetApp);
    console.log('  - Return URL:', finalReturnUrl);

    // Step 1: Generate PKCE pair
    console.log('\n[SSO] Step 1: Generating PKCE pair...');
    const { verifier, challenge } = await generatePKCEPair();
    console.log('  ✅ PKCE pair generated');

    // Step 2: Store verifier in sessionStorage
    console.log('\n[SSO] Step 2: Storing verifier...');
    storePKCEVerifier(verifier);
    console.log('  ✅ Verifier stored');

    // Step 3: Call backend to create SSO session
    console.log('\n[SSO] Step 3: Creating SSO session...');
    const response = await axios.post('/api/sso/create-session', {
      returnUrl: finalReturnUrl,
      codeChallenge: challenge,
      codeVerifier: verifier
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const { sessionId, expiresIn } = response.data;
    console.log('  ✅ Session created:', sessionId);

    // Step 4: Redirect to target app
    const authCallbackUrl = `${targetUrl.replace(/\/$/, '')}/auth-callback?session=${sessionId}`;
    console.log('\n[SSO] Step 4: Redirecting...');
    console.log('  - Target URL:', authCallbackUrl);

    window.location.href = authCallbackUrl;

  } catch (error) {
    console.error('❌ [SSO] Error during SSO flow:', error);

    if (error.response?.status === 401) {
      console.error('⚠️  User not authenticated');
      window.location.href = '/auth/login';
      return;
    }

    if (error.response?.status === 429) {
      alert('Troppe richieste. Attendi un momento e riprova.');
      return;
    }

    alert('Errore durante l\'autenticazione. Riprova.');
    throw error;
  }
}

/**
 * Handle external link click (SSO or direct)
 * @param {string} url - The URL to navigate to
 * @param {Event} event - The click event (optional, for preventDefault)
 */
export function handleExternalLink(url, event) {
  if (event) {
    event.preventDefault();
  }

  // Check if it's the Summus App URL (SSO flow)
  const appUrl = (process.env.REACT_APP_APP_URL || 'http://app.summus_network.localhost').replace(/\/$/, '');

  if (url === appUrl || url.startsWith(appUrl)) {
    // Use SSO flow
    initiateSSOToApp(url);
  } else {
    // Regular external link
    window.open(url, '_blank');
  }
}
